import { useEffect, useRef, useState } from "react";
import {
  addMileageMenu,
  deleteMileageMenu,
  editMileageMenu,
  getMileageMenu,
  MileageMenuResponse,
  updatePubProductOrders,
} from "../../../../../../../../api/pub-product";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import { MileageHeader } from "../../../../style";
import { useRecoilValue } from "recoil";
import { pubState } from "../../../../../../../../recoil/pub.atom";
import {
  MenuBottomWrapper,
  MenuItem,
  MenuListWrapper,
  MenuManageWrapper,
  MenuInputWrapper,
  MenuInputRow,
  MenuInputLabel,
  MenuInput,
  MileageMenuBodyWrapper,
  MileageMenuManage,
  MenuImageDropZoneWrapper,
} from "../../style";
import { Title } from "../../../../../../../../styles/page";
import ImageDropzone from "../../../../../../../../component/ImageDropZone";
import { ImageDropInitialContent } from "../../../../../../../reservation/styles/createReservationPopup";
import { Overlay } from "../../../../../../../../styles/popup";

interface MenuManageProps {
  onClose: () => void;
}

const MenuManage = ({ onClose }: MenuManageProps) => {
  const [list, setList] = useState<MileageMenuResponse[]>([]);
  const pub = useRecoilValue(pubState);
  const inputRef1 = useRef<HTMLInputElement | null>(null);
  const inputRef2 = useRef<HTMLInputElement | null>(null);
  const [menuName, setMenuName] = useState<string>("");
  const [menuPrice, setMenuPrice] = useState<number>(0);
  const [menuImage, setMenuImage] = useState<File | null>(null);
  const [menuImageUrl, setMenuImageUrl] = useState<string | null>(null);

  const [selectItem, setSelectItem] = useState<MileageMenuResponse | null>(
    null,
  );

  const getData = async () => {
    if (!pub) return;
    try {
      const res = await getMileageMenu(pub.id);
      setList(res);
    } catch {
      setList([]);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (!selectItem) {
      // 생성 모드
      setMenuName("");
      setMenuPrice(0);
      setMenuImage(null);
      setMenuImageUrl(null);
      return;
    }
    // 수정 모드
    setMenuName(selectItem.name);
    setMenuPrice(selectItem.price);
    setMenuImageUrl(selectItem.imageUrl || null);
    setMenuImage(null);
  }, [selectItem]);

  // 드래그 앤 드롭으로 순서 변경
  const reorder = <T,>(
    list: T[],
    startIndex: number,
    endIndex: number,
  ): T[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination || !pub) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    // 같은 위치면 변경 없음
    if (sourceIndex === destinationIndex) return;

    // 리스트 순서 변경
    const newList = reorder(list, sourceIndex, destinationIndex);

    setList(newList);

    // API 호출하여 순서 업데이트
    try {
      const productOrders = newList.map((item, index) => ({
        id: item.id,
        order: index + 1,
      }));
      await updatePubProductOrders(pub.id, productOrders);
    } catch (error) {
      console.error("순서 업데이트 실패:", error);
      alert("순서 변경에 실패했습니다. 다시 시도해주세요.");
      // 실패 시 원래 데이터 다시 로드
      getData();
    }
  };

  const handleDelete = async (id: number) => {
    if (id === -1) {
      setMenuName("");
      setMenuPrice(0);
      return;
    }
    try {
      await deleteMileageMenu(id);
      getData();
    } catch (e) {
      alert("재시도해주세요.");
    }
  };

  const handleSave = async () => {
    if (!pub) return;
    if (!menuName || menuPrice < 0) {
      alert("모든 필드를 채워주세요.");
      return;
    }
    if (menuName.length > 10) {
      alert("상품명은 10글자를 초과할 수 없습니다.");
      return;
    }

    const data = {
      pubId: pub.id,
      name: menuName,
      price: menuPrice,
    };

    try {
      if (selectItem) {
        // 수정 모드
        await editMileageMenu(selectItem.id, data, menuImage || undefined);
      } else {
        // 생성 모드
        await addMileageMenu(data, menuImage || undefined);
      }
      getData();
      setMenuName("");
      setMenuPrice(0);
      setMenuImage(null);
      setMenuImageUrl(null);
      setSelectItem(null);
      if (!selectItem) {
        inputRef1.current?.focus();
      }
    } catch {
      alert("재시도해주세요.");
    }
  };

  return (
    <>
      <MileageMenuManage>
        <MileageHeader>
          <Title size={20}>주류 메뉴 관리</Title>
          <img
            src="/image-web/solution/close.svg"
            alt="close"
            onClick={() => onClose()}
          />
        </MileageHeader>
        <MileageMenuBodyWrapper>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="menu-list" type="MENU">
              {(provided) => (
                <MenuListWrapper
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {list.map((item, index) => (
                    <Draggable
                      key={item.id}
                      draggableId={item.id.toString()}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <MenuItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          onClick={() => {
                            setSelectItem(item);
                          }}
                          className={
                            selectItem?.id === item.id ? "selected" : ""
                          }
                          style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.8 : 1,
                            cursor: snapshot.isDragging ? "grabbing" : "grab",
                            boxShadow: snapshot.isDragging
                              ? "0 5px 15px rgba(0, 0, 0, 0.3)"
                              : "none",
                            zIndex: snapshot.isDragging ? 9999 : "auto",
                          }}
                        >
                          <div className="name">{item.name}</div>
                          <img
                            src="/image-web/menu/delete.svg"
                            alt="delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item.id);
                            }}
                          />
                        </MenuItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </MenuListWrapper>
              )}
            </Droppable>
          </DragDropContext>
          <MenuManageWrapper>
            <MenuImageDropZoneWrapper>
              <ImageDropzone
                onImageUpload={(file) => {
                  setMenuImage(file);
                  setMenuImageUrl(URL.createObjectURL(file));
                }}
                defaultUrl={menuImageUrl || undefined}
              >
                <ImageDropInitialContent>
                  <img src="/image-web/icon/image.svg" alt="Initial content" />
                  <h2>주류 및 음료의 이미지를 올려주세요!</h2>
                </ImageDropInitialContent>
              </ImageDropzone>
            </MenuImageDropZoneWrapper>
            <MenuInputWrapper>
              <MenuInputRow>
                <MenuInputLabel>주류 메뉴</MenuInputLabel>
                <MenuInput
                  ref={inputRef1}
                  type="text"
                  placeholder="메뉴명"
                  value={menuName}
                  onChange={(e) => setMenuName(e.target.value)}
                />
              </MenuInputRow>
              <MenuInputRow>
                <MenuInputLabel>주류 메뉴 금액</MenuInputLabel>
                <MenuInput
                  ref={inputRef2}
                  type="number"
                  placeholder="가격"
                  value={menuPrice >= 0 ? menuPrice : ""}
                  onChange={(e) => {
                    if (+e.target.value < 0) return;
                    setMenuPrice(Number(e.target.value));
                  }}
                />
              </MenuInputRow>
            </MenuInputWrapper>
          </MenuManageWrapper>
        </MileageMenuBodyWrapper>
        <MenuBottomWrapper>
          <div
            className="button gray"
            onClick={() => {
              setSelectItem(null);
              setMenuName("");
              setMenuPrice(0);
              setMenuImage(null);
              setMenuImageUrl(null);
            }}
          >
            초기화
          </div>
          <div className="button purple" onClick={handleSave}>
            {selectItem ? "메뉴 수정하기" : "메뉴 생성하기"}
          </div>
        </MenuBottomWrapper>
      </MileageMenuManage>
      <Overlay />
    </>
  );
};

export default MenuManage;
